import type Highcharts from "highcharts";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Setup } from "../../../../api/useSetups";
import computeSymptom from "../../../../domain/computeSymptom";
import t from "../../../../i18n";
import { INTERNAL_GAP_ID } from "./getInternalGapSerie";
import seriesColors from "./seriesColors";
import type { MetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import { indexBy } from "diagnostics-lib/iterable";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import type { QuantityIndex } from "../../../../api/useQuantities";

const fillColor: Highcharts.GradientColorObject = {
  linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
  stops: [
    [0, seriesColors.symptom], // start
    [1, seriesColors.fadedSymptom], // end
  ],
};

export default function getSymptomSerie({
  analyses,
  setups,
  yAxisId,
  legendIndex,
  metrologyOverrides,
  quantityIndex,
}: {
  analyses: ActiveAnalysis[];
  setups: Setup[];
  yAxisId: string;
  legendIndex: number;
  metrologyOverrides: MetrologyOverrides;
  quantityIndex: QuantityIndex;
}): Highcharts.SeriesArearangeOptions {
  const setupIndex = setups.reduce<{
    [k: string]: {
      observables: { quantityId: string; sigma: number }[];
      simulationOutputByQuantityId: Map<
        string /*quantityId*/,
        { offsetExpected: number | undefined }
      >;
    };
  }>((map, setup) => {
    const observables = setup.diagnosisInputs.filter(d => d.enabled);
    const simulationOutputByQuantityId = indexBy(setup.simulationOutputs, "quantityId");

    map[setup.id] = { observables, simulationOutputByQuantityId };
    return map;
  }, {});

  const data: Highcharts.PointOptionsObject[] = analyses.reduce<Highcharts.PointOptionsObject[]>(
    (points, analysis, index) => {
      const previousAnalysis = analyses[index - 1];
      const setupId = getAnalysisSetup(setups, analysis)?.id;
      if (!setupId) return points;
      const setup = setupIndex[setupId];
      if (!setup) throw new Error(`No setup found for analysis ${analysis.id}`);

      if (previousAnalysis !== undefined && previousAnalysis.group !== analysis.group) {
        points.push({
          x: (previousAnalysis.date + analysis.date) / 2,
          high: undefined,
          low: undefined,
        });
      }

      points.push({
        x: analysis.date,
        high: computeSymptom(
          analysis,
          quantityIndex,
          metrologyOverrides,
          setup.observables,
          setup.simulationOutputByQuantityId,
        ),
        low: 0,
      });

      return points;
    },
    [],
  );

  return {
    id: INTERNAL_GAP_ID, // in order to have same visible series
    yAxis: yAxisId,
    name: t("charts.legend.symptom"),
    color: seriesColors.symptom,
    fillColor,
    type: "arearange" as const,
    replicateInNavigator: true,
    data,
    showInLegend: true,
    legendIndex,
  };
}
